import React, { useState } from 'react';
import ReactReadMoreReadLess from 'react-read-more-read-less';
import { Collapse } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { KTSVG } from '../../../_metronic/helpers';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';
import { FaEnvelope, FaFileAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import ViewOcrDetailsModal from './ViewOcrDetailsModal';
import { constraintConfig } from '../../../constraintConfig';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { canEditLineItem, canMergeLineItem, canUnmergeLineItem, getAttachedReceiptExpenseId, getAttachedReceiptLineItemId, getAttachedReceiptExpenseTrackingId, parseAttachedLineItemRemark } from './allReceiptsActionHelpers';

interface InvoiceDraftDetailProps {
    data: any;
    isLoading?: boolean;
    onClose?: () => void;
    onImageClick?: (url: string) => void;
    onRetry?: () => void;
    onViewReport?: () => void;
    onViewLineItem?: (expenseItemId: number) => void;
    onCompareOcrLineItem?: () => void;
    onCopyAttachmentUrl?: () => void;
    onViewViolations?: () => void;
    onMergeLineItem?: () => void;
    onUnmergeLineItem?: () => void;
    onEditLineItem?: () => void;
    /** Org Admin / Admin / Support — Compare OCR, View OCR Text, Confidence Score */
    showOcrDiagnostics?: boolean;
}

const isMsgOrEml = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lower = fileURL.toLowerCase();
    return lower.endsWith('.msg') || lower.endsWith('.eml');
};

const isHeicFile = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lower = fileURL.toLowerCase();
    return lower.endsWith('.heic') || lower.endsWith('.heif');
};

const getFileExtension = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const ext = fileURL.toLowerCase().split('.').pop();
    return ext?.toUpperCase() || 'File';
};

const getFileNameFromURL = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const parts = fileURL.split('/');
    return parts[parts.length - 1] || 'File';
};

const NON_ZOOMABLE_TITLE =
    'This is not zoomable, just click and can open in new tab / download';

const isPdfFile = (fileURL: string): boolean =>
    !!fileURL && fileURL.toLowerCase().endsWith('.pdf');

const isZoomableImage = (fileURL: string): boolean =>
    !!fileURL && !isPdfFile(fileURL) && !isMsgOrEml(fileURL) && !isHeicFile(fileURL);

const InvoiceDraftDetail: React.FC<InvoiceDraftDetailProps> = ({
    data,
    isLoading,
    onClose,
    onImageClick,
    onRetry,
    onViewReport,
    onViewLineItem,
    onCompareOcrLineItem,
    onCopyAttachmentUrl,
    onViewViolations,
    onMergeLineItem,
    onUnmergeLineItem,
    onEditLineItem,
    showOcrDiagnostics = false,
}) => {
    const [isItemDetailsOpen, setIsItemDetailsOpen] = useState(true);
    const [isReportDetailsOpen, setIsReportDetailsOpen] = useState(true);
    const [showOcrModal, setShowOcrModal] = useState(false);
    // Keep showing existing/list-row data while a refresh is in flight
    if (isLoading && !data) {
        return (
            <div className="card h-100">
                <div className="card-body d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="card h-100">
                <div className="card-body d-flex align-items-center justify-content-center">
                    <p className="invoice-draft-detail-empty mb-0">Select an invoice to view details</p>
                </div>
            </div>
        );
    }

    const getStatusBadge = (status: number) => {
        const statusMap: { [key: number]: { label: string; className: string } } = {
            0: { label: 'Processing', className: 'badge-light-warning' },
            1: { label: 'Ready To Use', className: 'badge-light-success' },
            2: { label: 'Failed', className: 'badge-light-danger' },
            3: { label: 'Attached', className: 'badge-light-primary' },
            4: { label: 'Submitted', className: 'badge-info' },
            5: { label: 'Already Used', className: 'badge-warning' },
            6: { label: 'Future Receipt', className: 'badge-light-info' },
            7: { label: 'CC Receipt', className: 'badge-light-info' },
        };
        const info = statusMap[status] || { label: 'Unknown', className: 'badge-light-dark' };
        return <span className={`badge ${info.className} fs-7 fw-semibold`}>{info.label}</span>;
    };

    const getRecordSource = (sourceId?: number) => {
        const id = sourceId ?? 0;
        let icon: React.ReactNode = null;
        let label = 'N/A';

        switch (id) {
            case constraintConfig.recordSourceId.WebApp:
                icon = (
                    <KTSVG
                        path="/media/icons/figmaIcons/Web.svg"
                        className="svg-icon-1"
                        data-tooltip-id="receipt-detail-source"
                        data-tooltip-content="Website"
                    />
                );
                label = 'Web App';
                break;
            case constraintConfig.recordSourceId.Mobile:
                icon = (
                    <KTSVG
                        path="/media/icons/figmaIcons/Mobile.svg"
                        className="svg-icon-1"
                        data-tooltip-id="receipt-detail-source"
                        data-tooltip-content="Mobile"
                    />
                );
                label = 'Mobile App';
                break;
            case constraintConfig.recordSourceId.BankFeed:
                icon = (
                    <KTSVG
                        path="/media/icons/figmaIcons/Bank.svg"
                        className="svg-icon-1"
                        data-tooltip-id="receipt-detail-source"
                        data-tooltip-content="Bank"
                    />
                );
                label = 'Bank Feed';
                break;
            case constraintConfig.recordSourceId.GorillaExpense:
                icon = (
                    <KTSVG
                        path="/media/icons/figmaIcons/Gorilla.svg"
                        className="svg-icon-1"
                        data-tooltip-id="receipt-detail-source"
                        data-tooltip-content="Gorilla Expense"
                    />
                );
                label = 'Gorilla Expense';
                break;
            default:
                return <span className="text-muted">N/A</span>;
        }

        return (
            <span className="d-inline-flex align-items-center gap-2">
                {icon}
                <span>{label}</span>
            </span>
        );
    };

    const isValidDate = (dateStr?: string) => {
        if (!dateStr) return false;
        return dateStr !== '0001-01-01T00:00:00';
    };

    // Render label + value in a row, exactly like the grid card style
    const renderField = (
        label: string,
        value: React.ReactNode,
        colWidth: number = 6,
        isMultiline: boolean = false
    ) => {
        return (
            <div className={`col-${colWidth} mb-1 ss-receipt-detail-field`}>
                <div className={`d-flex ${isMultiline ? 'align-items-start' : 'align-items-center'}`}>
                    <h5 className="ss-receipt-detail-field__label card-title fw-bold m-0 me-2 flex-shrink-0">
                        {label}:
                    </h5>
                    <p
                        className={`ss-receipt-detail-field__value mb-0 min-w-0 flex-grow-1 ${isMultiline ? 'text-wrap' : 'text-truncate'}`}
                        style={isMultiline ? { wordBreak: 'break-word', whiteSpace: 'normal' } : undefined}
                        title={!isMultiline && typeof value === 'string' ? value : undefined}
                    >
                        {value || 'N/A'}
                    </p>
                </div>
            </div>
        );
    };

    const previewAttachmentTooltip = {
        'data-tooltip-id': 'receipt-detail-preview',
        'data-tooltip-content': 'Click here for preview attachment',
        'aria-label': 'Click here for preview attachment',
    };

    const nonZoomableTooltip = {
        'data-tooltip-id': 'receipt-detail-preview',
        'data-tooltip-content': NON_ZOOMABLE_TITLE,
        'aria-label': NON_ZOOMABLE_TITLE,
        title: NON_ZOOMABLE_TITLE,
    };

    const renderFilePreview = (fileURL: string) => {
        const isMsgOrEmlFile = isMsgOrEml(fileURL);
        const isHeic = isHeicFile(fileURL);
        const fileName = getFileNameFromURL(fileURL);
        const ext = getFileExtension(fileURL);
        const canZoom = isZoomableImage(fileURL);

        if (isPdfFile(fileURL)) {
            return (
                <div
                    className="ss-receipt-detail-preview__media ss-receipt-detail-preview__media--no-zoom position-relative w-100 h-100"
                    {...nonZoomableTooltip}
                    onClick={(e) => {
                        e.stopPropagation();
                        onImageClick?.(fileURL);
                    }}
                >
                    <embed
                        src={`${fileURL}#toolbar=0&navpanes=0&scrollbar=0`}
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        className="cursor-pointer"
                    />
                </div>
            );
        }

        if (isMsgOrEmlFile) {
            return (
                <div
                    className="ss-receipt-detail-preview__media ss-receipt-detail-preview__media--no-zoom d-flex flex-column align-items-center justify-content-center p-2 h-100"
                    {...nonZoomableTooltip}
                    onClick={(e) => {
                        e.stopPropagation();
                        onImageClick?.(fileURL);
                    }}
                >
                    <FaEnvelope className="text-primary" style={{ fontSize: '36px' }} />
                    <h6 className="mt-2 mb-0 text-center px-1 w-100 text-truncate fs-8">{fileName}</h6>
                    <span className="badge bg-primary mt-1">{ext}</span>
                    <p className="text-muted mt-1 mb-0"><small>Click to open</small></p>
                </div>
            );
        }

        if (isHeic) {
            return (
                <div
                    className="ss-receipt-detail-preview__media ss-receipt-detail-preview__media--no-zoom d-flex flex-column align-items-center justify-content-center p-2 h-100"
                    {...nonZoomableTooltip}
                    onClick={(e) => {
                        e.stopPropagation();
                        onImageClick?.(fileURL);
                    }}
                >
                    <FaFileAlt className="text-warning" style={{ fontSize: '36px' }} />
                    <h6 className="mt-2 mb-0 text-center px-1 w-100 text-truncate fs-8">{fileName}</h6>
                    <span className="badge bg-warning mt-1">{ext}</span>
                    <p className="text-muted mt-1 mb-0"><small>Click to open</small></p>
                </div>
            );
        }

        return (
            <img
                className={`ss-receipt-detail-preview__media w-100 h-100 cursor-pointer${canZoom ? ' ss-receipt-detail-preview__media--zoomable' : ''}`}
                src={fileURL}
                alt="Receipt preview"
                {...previewAttachmentTooltip}
                onClick={(e) => {
                    e.stopPropagation();
                    onImageClick?.(fileURL);
                }}
                onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'media/svg/files/blank-image.svg';
                    e.currentTarget.classList.remove('ss-receipt-detail-preview__media--zoomable');
                    e.currentTarget.classList.add('ss-receipt-detail-preview__media--no-zoom');
                    e.currentTarget.setAttribute('title', NON_ZOOMABLE_TITLE);
                    e.currentTarget.setAttribute('data-tooltip-content', NON_ZOOMABLE_TITLE);
                }}
            />
        );
    };

    return (
        <div className="card h-100 ss-receipt-detail-panel">
            <Tooltip id="receipt-detail-source" place="top" />
            <Tooltip id="receipt-detail-actions" place="top" positionStrategy="fixed" />
            <Tooltip id="receipt-detail-preview" place="top" positionStrategy="fixed" />
            <div className="card-header border-0 py-3 px-4 d-flex align-items-center justify-content-between min-h-auto">
                <div className="d-flex align-items-center gap-2">
                    <h5 className="card-title fw-bold mb-0 fs-6">Receipt Details</h5>
                    {data?.ocrReadStatus === 2 && data?.noOfRetry <= 2 && onRetry && (
                        <button
                            type="button"
                            className="btn btn-sm rounded-pill"
                            style={{
                                padding: '0.35rem 0.8rem',
                                fontSize: '0.75rem',
                                color: '#856404',
                                backgroundColor: 'transparent',
                                border: '1px solid #ffc107',
                            }}
                            onClick={onRetry}
                        >
                            Retry ({data.noOfRetry})
                        </button>
                    )}
                </div>
                {onClose && (
                    <button
                        type="button"
                        className="btn btn-sm btn-icon btn-light-danger"
                        aria-label="Close"
                        data-tooltip-id="receipt-detail-actions"
                        data-tooltip-content="Close"
                        onClick={onClose}
                    >
                        <KTSVG path="/media/icons/duotune/arrows/arr088.svg" className="svg-icon-2" />
                    </button>
                )}
            </div>

            <div className="card-body pt-2 px-4 pb-4">
                <div className="ss-receipt-detail-hero mb-2 pb-2">
                    <div className="ss-receipt-detail-hero__meta">
                        {renderField('Status', getStatusBadge(data.ocrReadStatus), 12)}
                        {renderField('Retry Count', data.noOfRetry ?? 0, 12)}
                        {renderField('Uploaded From', getRecordSource(data.recordSourceID), 12)}
                        {renderField('Uploaded Date', data.createdDT ? <DateTimeComp formattedDate={data.createdDT} /> : 'N/A', 12)}
                        {showOcrDiagnostics &&
                            renderField(
                                'Confidence Score',
                                data.metadataConfidence != null && data.metadataConfidence !== ''
                                    ? `${
                                          Number(data.metadataConfidence) <= 1
                                              ? Math.round(Number(data.metadataConfidence) * 100)
                                              : Math.round(Number(data.metadataConfidence))
                                      }%`
                                    : '—',
                                12
                            )}
                    </div>
                    <div
                        className={`ss-receipt-detail-hero__preview${
                            data.fileURL && isZoomableImage(data.fileURL)
                                ? ' ss-receipt-detail-hero__preview--zoomable'
                                : ' ss-receipt-detail-hero__preview--no-zoom'
                        }`}
                    >
                        {data.fileURL ? (
                            renderFilePreview(data.fileURL)
                        ) : (
                            <div className="symbol symbol-80px symbol-circle bg-light-primary mx-auto d-flex align-items-center justify-content-center h-100">
                                <KTSVG path="/media/icons/duotune/files/fil014.svg" className="svg-icon-3x text-primary" />
                            </div>
                        )}
                    </div>
                </div>

                {(data.remarks || getAttachedReceiptLineItemId(data)) && (() => {
                    const attached = parseAttachedLineItemRemark(data.remarks);
                    const attachedLineItemId = getAttachedReceiptLineItemId(data);
                    const attachedExpenseId = getAttachedReceiptExpenseId(data);
                    const attachedExpenseTrackingId = getAttachedReceiptExpenseTrackingId(data);
                    const trackingId = data.trackingId || attached?.reportId;
                    const category = data.category || attached?.category;
                    const vendor = data.itemVendorName || data.vendorName;
                    const amountNode = data.expenseItemAmount
                        ? <CurrencyComp amount={data.expenseItemAmount} />
                        : attached?.amount;
                    const dateNode = isValidDate(data.expenseItemDate)
                        ? <DateTimeComp formattedDate={data.expenseItemDate} />
                        : attached?.date;
                    const paymentType = data.expensePayment?.title || attached?.paymentType;
                    const showStructured = !!(attached || attachedLineItemId);
                    const hasReport = !!attachedExpenseId && !!trackingId;

                    return (
                    <div className="alert alert-warning d-flex align-items-start p-3 mb-2 ss-receipt-attached-note" role="alert">
                        <KTSVG path="/media/icons/duotune/general/gen044.svg" className="svg-icon-2 me-2 mt-1 flex-shrink-0" />
                        {showStructured ? (
                            <div className="ss-receipt-attached-note__body">
                                <div className="ss-receipt-attached-note__header">
                                    <p className="mb-0 fw-semibold">
                                        {hasReport ? (
                                            <>
                                                Your Receipt is already attached to Expense Tracking ID:{' '}
                                                <span
                                                    className="text-primary cursor-pointer"
                                                    data-tooltip-id="receipt-detail-actions"
                                                    data-tooltip-content="View Expense Report for the line item this receipt is already attached to"
                                                    title="View Expense Report for the line item this receipt is already attached to"
                                                    onClick={() => attachedExpenseId && onViewReport?.()}
                                                >
                                                    {trackingId}
                                                </span>
                                            </>
                                        ) : (
                                            'This receipt is already attached to a line item.'
                                        )}
                                    </p>
                                    <div className="ss-receipt-attached-note__actions">
                                        {attachedLineItemId ? (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                data-tooltip-id="receipt-detail-actions"
                                                data-tooltip-content="View Used Line item for already attached this receipt"
                                                title="View Used Line item for already attached this receipt"
                                                aria-label="View Used Line item for already attached this receipt"
                                                onClick={() => onViewLineItem?.(attachedLineItemId)}
                                            >
                                                View Used Line Item
                                            </button>
                                        ) : null}
                                        {attachedExpenseId ? (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-info"
                                                data-tooltip-id="receipt-detail-actions"
                                                data-tooltip-content="View Expense Report for the line item this receipt is already attached to"
                                                title="View Expense Report for the line item this receipt is already attached to"
                                                aria-label="View Expense Report for the line item this receipt is already attached to"
                                                onClick={() => onViewReport?.()}
                                            >
                                                View Used Expense Report
                                            </button>
                                        ) : null}
                                        {onMergeLineItem &&
                                        canMergeLineItem({
                                            ...data,
                                            expenseItemId: data.expenseItemId || attachedLineItemId,
                                        }) ? (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-success"
                                                data-tooltip-id="receipt-detail-actions"
                                                data-tooltip-content="Merge to Existing Line Item"
                                                title="Merge to Existing Line Item"
                                                aria-label="Merge to Existing Line Item"
                                                onClick={() => onMergeLineItem()}
                                            >
                                                Merge to Existing Line Item
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                {(category || vendor || data.expenseType?.title) ? (
                                    <div className="ss-receipt-attached-note__meta">
                                        {category ? (
                                            <span>
                                                <strong>Category:</strong> {category}
                                            </span>
                                        ) : null}
                                        {vendor ? (
                                            <span>
                                                <strong>Vendor:</strong> {vendor}
                                            </span>
                                        ) : null}
                                        {data.expenseType?.title ? (
                                            <span>
                                                <strong>Expense Type:</strong> {data.expenseType.title}
                                            </span>
                                        ) : null}
                                    </div>
                                ) : null}
                                <div className="ss-receipt-attached-note__meta">
                                    {dateNode ? (
                                        <span><strong>Item Date:</strong> {dateNode}</span>
                                    ) : null}
                                    {paymentType ? (
                                        <span><strong>Payment type:</strong> {paymentType}</span>
                                    ) : null}
                                    {amountNode ? (
                                        <span><strong>Amount:</strong> {amountNode}</span>
                                    ) : null}
                                </div>
                            </div>
                        ) : (
                            <span className="ss-receipt-attached-note__body">{data.remarks}</span>
                        )}
                    </div>
                    );
                })()}

                <div className="border-bottom mb-2 pb-2">
                    <h6 className="fs-7 fw-bold mb-1 text-primary">Receipt Info</h6>
                    <div className="row g-1">
                        {renderField('Receipt No', data.invoiceNo)}
                        {renderField('Receipt Vendor', data.vendorName)}
                        {renderField('Amount', data.amount ? <CurrencyComp amount={data.amount} /> : '0.00')}
                        {renderField('Payment Method', data.expensePayment?.title)}
                        {renderField('Receipt Date', isValidDate(data.invoiceDate) ? <DateTimeComp formattedDate={data.invoiceDate} /> : 'N/A', 6)}
                        {renderField(
                            'Description',
                            data.readDocShortDesc ? (
                                <ReactReadMoreReadLess
                                    charLimit={200}
                                    readMoreText="Read more"
                                    readLessText="Read less"
                                    readMoreClassName="readMore"
                                    readLessClassName="readLess"
                                >
                                    {data.readDocShortDesc}
                                </ReactReadMoreReadLess>
                            ) : (
                                'N/A'
                            ),
                            12,
                            true
                        )}
                    </div>
                </div>

                <div className="border-bottom mb-2 pb-2">
                    <h6 className="fs-7 fw-bold mb-1 text-primary">👤 User Details</h6>
                    <div className="row g-1">
                        {renderField('SS User ID', data.orgUserId)}
                        {renderField('User Name', data.orgUserName)}
                    </div>
                </div>

                <div className="border-bottom mb-2 pb-2">
                    <button
                        className="btn btn-sm btn-link text-start p-0 mb-1 d-flex justify-content-between align-items-center w-100"
                        style={{ fontWeight: 600 }}
                        onClick={() => setIsItemDetailsOpen((prev) => !prev)}
                    >
                        <span className="fs-7 text-primary">🧾 SS Expense Item Details</span>
                        {isItemDetailsOpen ? (
                            <FaChevronUp className="text-primary" />
                        ) : (
                            <FaChevronDown className="text-primary" />
                        )}
                    </button>
                    <Collapse in={isItemDetailsOpen}>
                        <div>
                            <div className="row">
                                {renderField('Expense Item ID', data.expenseItemId)}
                                {renderField('Expense Category', data.category)}
                                {renderField('Payment Method', data.expensePayment?.title)}
                                {renderField('Expense Type', data.expenseType?.title)}
                                {renderField('Charge Location', data.expenseItemLocation?.title)}
                                {renderField('Item Vendor', data.itemVendorName || data.vendorName)}
                                {renderField('Item Date', isValidDate(data.expenseItemDate) ? <DateTimeComp formattedDate={data.expenseItemDate} /> : 'N/A', 6)}
                                {renderField('Item Amount', data.expenseItemAmount ? <CurrencyComp amount={data.expenseItemAmount} /> : 'N/A')}
                                {renderField('Item Source', getRecordSource(data.lineItemRecordSourceId))}
                                {renderField('Item Created Date', isValidDate(data.expenseItemCreatedDate) ? <DateTimeComp formattedDate={data.expenseItemCreatedDate} /> : 'N/A', 6)}
                            </div>
                        </div>
                    </Collapse>
                </div>

                <div className="border-bottom mb-3 pb-3">
                    <button
                        className="btn btn-sm btn-link text-start p-0 mb-2 d-flex justify-content-between align-items-center w-100"
                        style={{ fontWeight: 600 }}
                        onClick={() => setIsReportDetailsOpen((prev) => !prev)}
                    >
                        <span className="fs-7 text-primary">📊 SS Expense Report Details</span>
                        {isReportDetailsOpen ? (
                            <FaChevronUp className="text-primary" />
                        ) : (
                            <FaChevronDown className="text-primary" />
                        )}
                    </button>
                    <Collapse in={isReportDetailsOpen}>
                        <div>
                            <div className="row">
                                {renderField(
                                    'Tracking ID',
                                    data.trackingId ? (
                                        <span
                                            className="text-primary cursor-pointer"
                                            title="Click to copy Tracking ID"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard
                                                    .writeText(String(data.trackingId))
                                                    .then(() => toast.success('Tracking ID copied'))
                                                    .catch(() => toast.error('Unable to copy Tracking ID'));
                                            }}
                                        >
                                            {data.trackingId}
                                        </span>
                                    ) : (
                                        'N/A'
                                    )
                                )}
                                {renderField('Report Status', data.expenseStatus?.title && <span className="badge badge-light-success">{data.expenseStatus.title}</span>)}
                                {renderField('Report Date', data.reportDate ? <DateTimeComp formattedDate={data.reportDate} /> : 'N/A')}
                                {renderField('Report Amount', data.reportAmount ? <CurrencyComp amount={data.reportAmount} /> : '0.00')}
                                {renderField('Report Title', data.reportTitle || 'N/A', 12, true)}
                            </div>
                        </div>
                    </Collapse>
                </div>

                <div>
                    <div className="ss-receipt-ocr-head">
                        <h6 className="fs-7 fw-bold mb-0 text-primary">🤖 OCR Details</h6>
                        <div className="ss-receipt-ocr-ids">
                        {data.trackingId && data.expenseId ? (
                            <span
                                className="badge badge-light-primary fs-7 cursor-pointer"
                                title="Click to copy Tracking ID"
                                data-tooltip-id="receipt-detail-actions"
                                data-tooltip-content="Click to copy Tracking ID"
                                onClick={() => {
                                    navigator.clipboard
                                        .writeText(String(data.trackingId))
                                        .then(() => toast.success('Tracking ID copied'))
                                        .catch(() => toast.error('Unable to copy Tracking ID'));
                                }}
                            >
                                Tracking ID: {data.trackingId}
                            </span>
                        ) : null}
                        {(data.expenseItemId || data.usedExpenseItemId) ? (
                            <span className="badge badge-light-info fs-7">
                                Expense Item ID: {data.expenseItemId || data.usedExpenseItemId}
                            </span>
                        ) : null}
                        </div>
                    </div>
                    <div className="ss-receipt-ocr-actions">
                        {data.trackingId && data.expenseId && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-primary"
                                onClick={() => onViewReport?.()}
                            >
                                View Report
                            </button>
                        )}
                        {(data.expenseItemId || data.usedExpenseItemId) && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-primary"
                                onClick={() =>
                                    onViewLineItem?.(
                                        Number(data.expenseItemId || data.usedExpenseItemId)
                                    )
                                }
                            >
                                View LineItem
                            </button>
                        )}
                        {(data.expenseItemId || data.usedExpenseItemId) && onCompareOcrLineItem && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-info"
                                onClick={() => onCompareOcrLineItem()}
                                data-tooltip-id="receipt-detail-actions"
                                data-tooltip-content="Compare OCR and Expense Item Values"
                                title="Compare OCR and Expense Item Values"
                                aria-label="Compare OCR and Expense Item Values"
                            >
                                Compare OCR VS Item
                            </button>
                        )}
                        {data.fileURL && onCopyAttachmentUrl && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-warning"
                                onClick={() => onCopyAttachmentUrl()}
                            >
                                Copy Attachment URL
                            </button>
                        )}
                        {!!data.isViolation && (data.expenseItemId || data.usedExpenseItemId) && onViewViolations && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-danger"
                                onClick={() => onViewViolations()}
                            >
                                View Violations
                            </button>
                        )}
                        {canEditLineItem(data) && onEditLineItem && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-info"
                                onClick={() => onEditLineItem()}
                            >
                                Edit LineItem
                            </button>
                        )}
                        {canMergeLineItem(data) && onMergeLineItem && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-success"
                                onClick={() => onMergeLineItem()}
                            >
                                Merge Line Item
                            </button>
                        )}
                        {canUnmergeLineItem(data) && onUnmergeLineItem && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-danger"
                                onClick={() => onUnmergeLineItem()}
                            >
                                UnMerge LineItem
                            </button>
                        )}
                        {data.fileURL && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-primary"
                                onClick={() => onImageClick?.(data.fileURL)}
                            >
                                View Receipt
                            </button>
                        )}
                        {showOcrDiagnostics && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-primary"
                                onClick={() => setShowOcrModal(true)}
                            >
                                View OCR Text
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <ViewOcrDetailsModal
                show={showOcrModal}
                handleClose={() => setShowOcrModal(false)}
                data={data}
                onViewLineItem={(expenseItemId) => {
                    setShowOcrModal(false);
                    onViewLineItem?.(expenseItemId);
                }}
                onViewReport={(expenseId?: number) => {
                    setShowOcrModal(false);
                    onViewReport?.();
                }}
            />
        </div>
    );
};

export default InvoiceDraftDetail;